import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useSession } from './hooks/use-session';
import { createTheme, ThemeProvider } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
  }

  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;   // 👈 añadido
    xxxl: true;  // 👈 añadido
  }
}
// Definimos el tipo del contexto
export interface PageContextProps {
  loadingPage: boolean;
  setLoadingPage: (loading: boolean) => void;
  session: any; //boolean;
  setSession: any; //(session:boolean) => void;
  loginView: any;
  setLoginView: any;
  esquemasCompra: any;
  setEsquemasCompra: any;
  copyrightPopUpOpen: any;
  setCopyrightPopUpOpen: any;
  copyrightPopUpSeen: {home: boolean, lotes: boolean, terminosCondiciones: boolean};
  setCopyrightPopUpSeen: any;
  openLoginModal: any;
  setOpenLoginModal: any;
  canCloseModal: any;
  setCanCloseModal: any;
  simulatorPreset: any;
  setSimulatorPreset: any;
}

const colorsAzul = {
  primaryMain: "#0048aa",
  primaryLight: "#336cbb",
  primaryDark: "#003276",
  secondaryMain: "#e6a93c",
  secondaryLight: "#ebba63",
  secondaryDark: "#a1762a",
  white: "#fff",
  lightBackground: "#f2f3f8"
};

const colorsRojo = {
  primaryMain: "#C8102E",
  primaryLight: "#c6455bff",
  primaryDark: "#8a051bff",
  secondaryMain: "#e6a93c",
  secondaryLight: "#ebba63",
  secondaryDark: "#a1762a",
  white: "#fff",
  lightBackground: "#f2f3f8"
};
const colors = colorsAzul;
const fonts = {
  base: "Lato, sans-serif",
  heading: "Merriweather",
};

const theme = createTheme({
  palette: {
    primary: {
      light: colors.primaryLight,
      main: colors.primaryMain,
      dark: colors.primaryDark,
      contrastText: colors.white,
    },
    secondary: {
      light: colors.secondaryLight,
      main: colors.secondaryMain,
      dark: colors.secondaryDark,
      contrastText: colors.white,
    },
    neutral: {
      main: '#eeeeee',
      light: '#fafafa',
      dark: '#e0e0e0',
      contrastText: '#212121',      
    }
  },
  typography: {
    fontFamily: fonts.base,
    h1: {
      fontFamily: fonts.heading,
      color: colors.primaryMain,
    },
    h2: {
      fontFamily: fonts.heading,
      color: colors.primaryMain,
      fontSize: "clamp(2rem, 4vw, 3.75rem)"
    },
    h3: {
      fontFamily: fonts.heading,
      color: colors.primaryMain,
    },
    h4: {
      fontSize: "clamp(1.50rem, 2vw, 2.125rem)"
    },
    subtitle1: {
      fontSize: "1.25rem"
    },
    body1: {
      fontSize: "1.25rem"
    },
    body2: {
      fontSize: "1rem"
    }
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        sizeMedium: {
          fontSize: "1.25rem"
        },
        sizeLarge: {
          fontSize: "1.5rem"
        }
      },
      defaultProps: {
        disableElevation: true,        
      }
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        disableGutters: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 4,
          overflow: 'clip',
          backgroundColor: 'hsl(0, 0%, 99%)',
          border: '1px solid',
          borderColor: 'hsla(220, 20%, 80%, 0.4)',
          ':before': {
            backgroundColor: 'transparent',
          },
          '&:not(:last-of-type)': {
            borderBottom: 'none',
          },
          '&:first-of-type': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
          '&:last-of-type': {
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 'none',
          borderRadius: 8,
          '&:hover': { backgroundColor: colors.lightBackground },
          '&:focus-visible': { backgroundColor: 'transparent' },
          ...theme.applyStyles('dark', {
            '&:hover': { backgroundColor: 'blue' },
          }),
          'span': {
            fontWeight: 600
          }        
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: { mb: 20, border: 'none' },
      },
    },
  },
   breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1900,   // 👈 nuevo breakpoint
      xxxl: 2400,  // 👈 otro opcional
    },
  },
});


// Creamos el contexto
export const PageContext = createContext<PageContextProps | undefined>(undefined);

// Crear un componente Provider que envuelve la aplicación
export const PageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  //const [session, setSession] = useState(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(true);
  const [session, setSession] = useSession();
  const [loginView, setLoginView] = React.useState(false);
  const [esquemasCompra, setEsquemasCompra] = React.useState<any[]>([]);
  const [copyrightPopUpOpen, setCopyrightPopUpOpen] = React.useState<boolean>(false);
  const [copyrightPopUpSeen, setCopyrightPopUpSeen] = React.useState({home: false, lotes: true, terminosCondiciones: false});
  const [openLoginModal, setOpenLoginModal] = React.useState(false);
  const [canCloseModal, setCanCloseModal] = React.useState(true);
  const [simulatorPreset, setSimulatorPreset] = React.useState(2);

  const props: PageContextProps = {
    loadingPage,
    setLoadingPage,
    session,
    setSession,
    loginView,
    setLoginView,
    esquemasCompra,
    setEsquemasCompra,
    copyrightPopUpOpen,
    setCopyrightPopUpOpen,
    copyrightPopUpSeen,
    setCopyrightPopUpSeen,
    openLoginModal,
    setOpenLoginModal,
    canCloseModal,
    setCanCloseModal,
    simulatorPreset,
    setSimulatorPreset,
  }

  return (
    <PageContext.Provider value={props}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </PageContext.Provider>
  );
};

// Crear un hook personalizado para acceder al contexto
export const usePageContext = () => {
  const context = useContext(PageContext);

  if (!context) {
    //throw new Error('usePageContext debe ser utilizado dentro de un PageProvider');
  }

  return context;
};